import React, { useState, useEffect } from 'react';
import { Grid, IconButton, Typography, Box, CssBaseline, Paper, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { getClients, getTrips, getFleetCount, dashboardVehicleSpeed,getWarehouses, getAverageKmPerVehicle, getTripStatus, getWarehouseCompanyMapping, getVendorClientWarehouseMapping, dashboardFleetCount, dashboardTotalKm, getDashboardVehicleCount } from '../Dashboard/action.ts';
import { SelectChangeEvent } from '@mui/material/Select';
import QueryWidget from '../../Components/QueryWidget/query-widget.tsx'
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, ChartDataLabels);

const getOrdinal = (n: number) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const monthNames: { [key: number]: string } = { 
  1: 'Jan', 2: 'Feb', 3: 'March', 4: 'April', 5: 'May', 6: 'June', 
  7: 'July', 8: 'Aug', 9: 'Sept', 10: 'Oct', 11: 'Nov', 12: 'Dec' 
};

const formatMonthLabel = (labelStr: string): string => {
  if (!labelStr) return '';
  
  if (/^\d{4}-\d{2}-\d{2}$/.test(labelStr)) {
    const d = new Date(labelStr);
    return `${getOrdinal(d.getDate())} ${monthNames[d.getMonth() + 1]}`;
  }
  if (/^\d{2}-\d{4}$/.test(labelStr)) {
    const m = parseInt(labelStr.split('-')[0], 10);
    return monthNames[m] || labelStr;
  }
  if (/^\d{4}-\d{2}$/.test(labelStr)) {
    const m = parseInt(labelStr.split('-')[1], 10);
    return monthNames[m] || labelStr;
  }
  const numericMonth = parseInt(labelStr, 10);
  if (!isNaN(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    return monthNames[numericMonth];
  }

  return labelStr;
};

const AverageKmPerVehicleToday = {
  labels: ['0', '50', '100', '150'],
  datasets: [{
    data: ['50', '50', '50'],
    backgroundColor: ['#e74c3c', '#f4d73f', '#52be80'],
    hoverBackgroundColor: ['#52ba78', '#f4d89f', '#e7ad45'],
  }],
};
const AverageKmPerVehicleMonth = {
  labels: ['0-50 Km', '50-100 Km', '100-150 Km'],
  datasets: [{
    data: [50, 50, 50],
    backgroundColor: ['#e74c3c', '#f4d73f', '#52be80'],
    hoverBackgroundColor: ['#52ba78', '#f4d89f', '#e7ad45'],
  }],
};

const doughnutOptions = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: {
      position: 'bottom' as const,
      onClick: null,
      labels: {
        boxWidth: 12,
      }
    },
    datalabels: {
      display: (ctx: any) => {
        const dataset = ctx.dataset?.data;
        if (!dataset) return false;
        const isZeroPointOne = dataset.some((value: number) => value === 0.01);
        return !isZeroPointOne;
      },
      font: {
        size: 14,
        weight: 'bold' as const
      }
    },
    tooltip: {
      enabled: false,
    },
  },
};

const halfDoughnutOptions = {
  maintainAspectRatio: false,
  responsive: true,
  cutout: '50%',
  rotation: 270,
  circumference: 180,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
    datalabels: { display: false },
  },
};

const stackedBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom" as const,
      labels: { padding: 5, boxWidth: 12 }
    },
    datalabels: {
      color: "#000",
      anchor: "center" as const,
      align: "center" as const,
      formatter: (value: number) => {
        return value === 0 ? "" : value;
      },
      font: { size: 10, weight: "bold" as const }
    },
  },
  layout: { padding: { top: 20 } },
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        stepSize: 1,
        callback: function (value: unknown) {
          return Number.isInteger(value) ? value : null;
        }
      }
    }
  }
};

const totalPlugin = {
  id: "totalPlugin",
  afterDatasetsDraw(chart: { getDatasetMeta?: any; ctx?: any; data?: any; }) {
    const { ctx, data } = chart;
    ctx.save();
    data.labels.forEach((_label: any, index: string | number) => {
      const total = data.datasets
        .map((ds: { data: { [x: string]: any; }; }) => ds.data[index])
        .reduce((a: any, b: any) => a + b, 0);

      const meta = chart.getDatasetMeta(data.datasets.length - 1);
      const bar = meta.data[index];
      ctx.font = "bold 12px sans-serif";
      ctx.fillStyle = "#000";
      ctx.textAlign = "center";
      ctx.fillText(total, bar.x, bar.y - 5);
    });
  }
};

const DashboardPage = () => {
  const [clientOptions, setClientOptions] = useState<{ id: string, label: string }[]>([]);
  const [date, setDate] = useState<string>('today');
  const [clientCount, setClientCount] = useState<string>('');
  const [warehouseOptions, setWarehouseOptions] = useState<{ id: string, label: string }[]>([]);
  const [warehouseCount, setWarehouseCount] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<{ id: string, name: string } | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<{ id: string, name: string } | null>(null);
  const [showEVStatus, setShowEVStatus] = useState(false);
  const [showICEStatus, setShowICEStatus] = useState(false);
  const [fleetCountDate, setFleetCountDate] = useState<string>('day-wise');
  const [nonEvVehicleCount, setNonEvVehicleCount] = useState<string>('');
  const [evVehicleCount, setEvVehicleCount] = useState<string>('');
  const [lowestDistanceToday, setLowestDistanceToday] = useState<string>('');
  const [HighestDistanceToday, setHighestDistanceToday] = useState<string>('');
  const [averageDistanceToday, setAverageDistanceToday] = useState<any>('');
  const [showHighestVehicle, setShowHighestVehicle] = useState(true);
  const [showAverageVehicle, setShowAverageVehicle] = useState(false);
  const [showTotalVehicle, setShowTotalVehicle] = useState(false);
  const [showTrips, setShowTrips] = useState(false)
  const [kmFuelType, setKmFuelType] = useState<string>('All');
  const [kmDateMode, setKmDateMode] = useState<string>('day-wise');
  const [dieselCount, setDieselCount] = useState<number>(0);
  const [dashboardEvCount, setDashboardEvCount] = useState<number>(0);
  const [cngCount, setCngCount] = useState<number>(0);
  const [petrolCount, setPetrolCount] = useState<number>(0);
  const [rawTotalKmData, setRawTotalKmData] = useState<any[]>([]);
  const [selectedKmMetric, setSelectedKmMetric] = useState<string>('Total');
  const kmMetricOptions = [
    { id: 'Lowest', label: 'Lowest Km' },
    { id: 'Highest', label: 'Highest Km' },
    { id: 'Average', label: 'Average Km' },
    { id: 'Total', label: 'Total Km' },
  ];
  
  const [isFuelChecked, setIsFuelChecked] = useState<boolean>(true);
  const [modelData, setModelData] = useState<{ make_model: string, total: number }[]>([]);
  const [isModelChecked, setIsModelChecked] = useState<boolean>(false);
  const [totalKmData, setTotalKmData] = useState({
    labels: [] as string[],
    datasets: [] as any[],
  });
  const [dailyVehicleCounts, setDailyVehicleCounts] = useState<{
    date: string,
    count: number,
    trip1Count: number,
    trip2Count: number,
    trip3Count: number
  }[]>([]);
  const [tripTimingData, setTripTimingData] = useState<number[]>([0, 0, 0, 0, 0, 0]);
  
  const [fleetPerMonth, setFleetPerMonth] = useState({
    labels: [] as string[],
    datasets: [] as any[],
  });
  const [dashboardFleetData, setDashboardFleetData] = useState({
    labels: [] as string[],
    datasets: [] as any[],
  });
  const [fleetCountTodayData, setFleetCountTodayData] = useState({
    labels: [] as string[],
    datasets: [
      {
        label: 'Running',
        data: [] as number[],
        backgroundColor: '#52be80',
      },
      {
        label: 'Not Running',
        data: [] as number[],
        backgroundColor: '#e74c3c',
      }
    ],
  });

  const [speedDateFilter, setSpeedDateFilter] = useState<string>('Today');
  const [speedMode, setSpeedMode] = useState<'average' | 'highest'>('average');
  const [customFromDate, setCustomFromDate] = useState<string>('');
  const [customToDate, setCustomToDate] = useState<string>('');

  const speedDateOptions = [
    { id: 'Today', label: 'Today' },
    { id: 'Yesterday', label: 'Yesterday' },
    { id: 'This Month', label: 'This Month' },
    { id: 'Last Month', label: 'Last Month' },
    { id: 'Custom', label: 'Custom' },
  ];

  const [vehicleSpeedData, setVehicleSpeedData] = useState({
    labels: [] as string[],
    datasets: [{
      label: 'Vehicle Speed',
      data: [] as number[],
      backgroundColor: '#4BC0C0',
    }]
  });

  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("userdetails");
  const savedClient = localStorage.getItem('lastSelectedClient');
  const savedWarehouse = localStorage.getItem('lastSelectedWarehouse');
  const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
  const userRole = userDetailsFromStorage?.role || '';
  const userCompaniesId = userDetailsFromStorage?.companies_id || '';
    const isCompanyDevice = parseInt(userCompaniesId) === 85 || parseInt(userCompaniesId) === 10;
  const roleFeatures = JSON.parse(localStorage.getItem('roleFeatures') || '[]');
  const hasFeature = (featureName: string) => {
    if (!roleFeatures) return false;
    const target = featureName.toLowerCase();
    const containsFeature = (arr: any[]) => arr.some((f: any) => 
      (typeof f === 'string' && f.toLowerCase() === target) ||
      (f?.name && f.name.toLowerCase() === target) ||
      (f?.feature && f.feature.toLowerCase() === target)
    );

    if (Array.isArray(roleFeatures)) {
      const configObj = roleFeatures.find(item => item && typeof item === 'object' && (item.enabled || item.visible));
      
      if (configObj) {
        if (configObj.enabled && Array.isArray(configObj.enabled) && containsFeature(configObj.enabled)) return true;
        if (configObj.visible && Array.isArray(configObj.visible) && containsFeature(configObj.visible)) return true;
      }
      
      return containsFeature(roleFeatures);
    }

    if (typeof roleFeatures === 'object') {
      if (roleFeatures.enabled && Array.isArray(roleFeatures.enabled) && containsFeature(roleFeatures.enabled)) return true;
      if (roleFeatures.visible && Array.isArray(roleFeatures.visible) && containsFeature(roleFeatures.visible)) return true;
      return !!roleFeatures[featureName] || !!roleFeatures[target];
    }

    return false;
  };

  const hasWarehouseFeature = hasFeature('warehouses');
  const hasClientFeature = hasFeature('clients');
  const hasDistanceGraph = hasFeature('dashboard_distance');
  const hasTripStatus = hasFeature('dashboard_trip_status');
  const hasVehicleFleetCount = hasFeature('dashboard_vehicle_fleet_count');
  const hasVehicleCount = hasFeature('dashboard_vehicle_count');
  const hasTotalKms = hasFeature('dashboard_total_kms');
  const hasVehicleSpeed = hasFeature('dashboard_vehicle_speed');
  const hasDeviceFleetCount = hasFeature('dashboard_device_fleet_count');
  const hasTripsFeature = hasFeature('trips');
  const hasVehiclesFeature = hasFeature('vehicles');

  const fuelOptions = [
    { id: 'All', label: 'All' },
    { id: 'EV', label: 'EV' },
    { id: 'Petrol', label: 'Petrol' },
    { id: 'Diesel', label: 'Diesel' },
  ];

  useEffect(() => {
    sessionStorage.removeItem('trips_active_tab');
    if (!isLoggedIn) {
      navigate('/');
      return;
    }

    if (savedClient) {
      setSelectedClient(JSON.parse(savedClient));
    }
    if (savedWarehouse) {
      setSelectedWarehouse(JSON.parse(savedWarehouse));
    }
    
    if (isLoggedIn) {
      if (hasClientFeature) {
        fetchClients();
      } else if (hasWarehouseFeature) {
        fetchWarehouses('');
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (hasDistanceGraph) {
      fetchAverageKmPerVehicle();
    }
  }, [date, selectedClient, selectedWarehouse, hasDistanceGraph]);
  
  useEffect(() => {
    if (hasTripStatus) {
      fetchTripStatus();
    }
  }, [showEVStatus, selectedClient, showICEStatus, selectedWarehouse, hasTripStatus]);

  const fetchTotalKmData = () => {
    const endDate = dayjs().format('YYYY-MM-DD');
    let startDate;

    const modeParam = kmDateMode === 'month-wise' ? 'month' : 'day';

    if (modeParam === 'month') {
      startDate = dayjs().startOf('year').format('YYYY-MM-DD'); 
    } else {
      startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    }

    const fuelParam = kmFuelType === 'All' ? '' : kmFuelType;

    dashboardTotalKm(modeParam, startDate, endDate, fuelParam, (response: any) => {
      if (response.status === 200) {
        setRawTotalKmData(response.data);
      }
    }, (error: any) => {
      console.error("Total KM Error:", error);
      setRawTotalKmData([]);
    });
  };

  const handleFuelCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsFuelChecked(true);
      setIsModelChecked(false);
    }
  };

  const handleModelCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsModelChecked(true);
      setIsFuelChecked(false);
    }
  };

  useEffect(() => {
    if (hasTotalKms) {
      fetchTotalKmData();
    }
  }, [kmFuelType, kmDateMode, selectedClient, selectedWarehouse, hasTotalKms]);

  useEffect(() => {
    const finalLabels: string[] = [];
    const dataPoints: number[] = [];
    const vehicleNumbers: string[] = [];
    const safeApiData = rawTotalKmData || [];

    if (kmDateMode === 'month-wise') {
      const currentMonth = dayjs().month(); 
      for (let i = 0; i <= currentMonth; i++) {
        finalLabels.push(monthNames[i + 1]);
      }
    } else {
      for (let i = 6; i >= 0; i--) {
        const d = dayjs().subtract(i, 'day');
        finalLabels.push(`${getOrdinal(d.date())} ${monthNames[d.month() + 1]}`);
      }
    }

    finalLabels.forEach(targetLabel => {
      const match = safeApiData.find(item => {
        const id = item._id;
        let itemLabel = '';
        if (typeof id === 'object' && id !== null && id.month && id.year) {
          if (id.day) {
            itemLabel = `${getOrdinal(id.day)} ${monthNames[id.month]}`;
          } else {
            itemLabel = `${monthNames[id.month]}`;
          }
        } else {
          itemLabel = formatMonthLabel(String(id));
        }
        return itemLabel === targetLabel;
      });

      if (match) {
        if (selectedKmMetric === 'Lowest') {
          dataPoints.push(Math.round(parseFloat(match.lowest_km || 0)));
          vehicleNumbers.push(match.lowest_km_vehicle_number || '');
        } else if (selectedKmMetric === 'Highest') {
          dataPoints.push(Math.round(parseFloat(match.highest_km || 0)));
          vehicleNumbers.push(match.highest_km_vehicle_number || '');
        } else if (selectedKmMetric === 'Average') {
          dataPoints.push(Math.round(parseFloat(match.avg_km || 0)));
          vehicleNumbers.push('');
        } else if (selectedKmMetric === 'Total') {
          dataPoints.push(Math.round(parseFloat(match.total_km_sum || 0)));
          vehicleNumbers.push('');
        }
      } else {
        dataPoints.push(0); 
        vehicleNumbers.push('');
      }
    });

    const datasets = [];
    if (selectedKmMetric === 'Lowest') {
      datasets.push({ label: 'Lowest KM', data: dataPoints, backgroundColor: '#4BC0C0', customVehicleNumbers: vehicleNumbers });
    } else if (selectedKmMetric === 'Highest') {
      datasets.push({ label: 'Highest KM', data: dataPoints, backgroundColor: '#36A2EB', customVehicleNumbers: vehicleNumbers });
    } else if (selectedKmMetric === 'Average') {
      datasets.push({ label: 'Average KM', data: dataPoints, backgroundColor: '#FF6384' });
    } else if (selectedKmMetric === 'Total') {
      datasets.push({ label: 'Total KM', data: dataPoints, backgroundColor: '#ffa726' });
    }

    setTotalKmData({ labels: finalLabels, datasets });
  }, [rawTotalKmData, kmDateMode, selectedKmMetric]);

  useEffect(() => {
    if (hasDeviceFleetCount) {
      const mode = fleetCountDate === 'month-wise' ? 'month' : 'day';

      dashboardFleetCount(mode, (response: any) => {
        if (response.status === 200) {
          const apiData = response.data || [];
          const finalLabels: string[] = [];
          const runningData: number[] = [];
          const notRunningData: number[] = [];

          if (mode === 'day') {
            for (let i = 6; i >= 0; i--) {
              const date = dayjs().subtract(i, 'day');
              const displayLabel = `${getOrdinal(date.date())} ${monthNames[date.month() + 1]}`;
              finalLabels.push(displayLabel);

              const match = apiData.find((item: any) => formatMonthLabel(String(item.label || '')) === displayLabel);
              
              runningData.push(match ? match.running : 0);
              notRunningData.push(match ? match.not_running : 0);
            }
          } else {
            const currentMonth = dayjs().month();
            for (let i = 0; i <= currentMonth; i++) {
              const displayLabel = monthNames[i + 1];
              finalLabels.push(displayLabel);

              const match = apiData.find((item: any) => formatMonthLabel(String(item.label || '')) === displayLabel);

              runningData.push(match ? match.running : 0);
              notRunningData.push(match ? match.not_running : 0);
            }
          }

          setDashboardFleetData({
            labels: finalLabels,
            datasets: [
              {
                label: 'Running',
                data: runningData,
                backgroundColor: '#42a5f5',
              },
              {
                label: 'Not Running',
                data: notRunningData,
                backgroundColor: '#ef5350',
              },
            ],
          });
        }
      }, (error: any) => {
        console.error("Fleet Count Error:", error);
      });
    }
  }, [selectedWarehouse, selectedClient, fleetCountDate, hasDeviceFleetCount]);

  useEffect(() => {
    if(hasVehicleSpeed) {
      let from_date = '';
      let to_date = '';
      const today = dayjs();

      switch (speedDateFilter) {
        case 'Today':
          from_date = today.format('YYYY-MM-DD');
          to_date = today.format('YYYY-MM-DD');
          break;
        case 'Yesterday':
          const yesterday = today.subtract(1, 'day');
          from_date = yesterday.format('YYYY-MM-DD');
          to_date = yesterday.format('YYYY-MM-DD');
          break;
        case 'This Month':
          from_date = today.startOf('month').format('YYYY-MM-DD');
          to_date = today.format('YYYY-MM-DD');
          break;
        case 'Last Month':
          const lastMonth = today.subtract(1, 'month');
          from_date = lastMonth.startOf('month').format('YYYY-MM-DD');
          to_date = lastMonth.endOf('month').format('YYYY-MM-DD');
          break;
        case 'Custom':
          from_date = customFromDate;
          to_date = customToDate;
          break;
      }

      if (speedDateFilter === 'Custom' && (!from_date || !to_date)) {
        return;
      }

      const mode = speedMode;
      const top = 5;

      dashboardVehicleSpeed(
        mode,
        from_date,
        to_date,
        top,
        (response: any) => {
          if (response && response.data) {
            const apiData = Array.isArray(response.data) ? response.data : [];
            const labels = apiData.map((item: any) => item.vehicle_number || 'Unknown');
            const data = apiData.map((item: any) => item.speed || 0);

            setVehicleSpeedData({
              labels: labels.length > 0 ? labels : ['No Data'],
              datasets: [{
                label: 'Speed',
                data: data.length > 0 ? data : [0],
                backgroundColor: '#4BC0C0',
              }]
            });
          }
        }, 
        (error: any) => { console.log("Vehicle Speed Error:", error) }
      );
    }
  }, [speedDateFilter, speedMode, customFromDate, customToDate, selectedWarehouse, selectedClient, hasVehicleSpeed]);

  useEffect(() => {
    if(hasVehicleCount) {
      const fuelFlag = isFuelChecked ? 1 : 0;
      const modelFlag = isModelChecked ? 1 : 0;

      getDashboardVehicleCount(fuelFlag, modelFlag, (response: any) => {
        if (response.status === 200 && response.data) {
          if (isModelChecked || response.type === "make_model") {
            setModelData(response.data);
          } else {
            let diesel = 0, ev = 0, cng = 0, petrol = 0;
            response.data.forEach((item: any) => {
              const type = (item.fuel_type || '').toLowerCase();
              if (type.includes('diesel')) diesel = item.total;
              else if (type.includes('ev')) ev = item.total;
              else if (type.includes('cng')) cng = item.total;
              else if (type.includes('petrol')) petrol = item.total;
            });

            setDieselCount(diesel);
            setDashboardEvCount(ev);
            setCngCount(cng);
            setPetrolCount(petrol);
          }
        }
      }, (error: any) => { console.log("Vehicle Count Error:", error) });
    }
  }, [selectedWarehouse, selectedClient, isFuelChecked, isModelChecked, hasVehicleCount]);

  useEffect(() => {
    if (hasTripsFeature) {
      fetchTrips();
    }
  }, [selectedClient, selectedWarehouse, hasTripsFeature]);

  const fetchClients = () => {
    getClients(
      (response: any) => {
        const options = response.message.clients_vendors.map((clients: any) => ({
          id: clients.id,
          label: clients.name || '',
        }));
        setClientOptions(options);
        setClientCount(response.total_clients_vendors);
      },
      (errMessage: any) => {
        toast.error(errMessage);
      }
    );
    if (hasWarehouseFeature) {
      fetchWarehouses(savedClient || '');
    }
  };

  const fetchWarehouses = (clientName: string) => {
   if (userRole === 'vendor_supervisor') {
        getWarehouses(
            0,      
            "100",
            "", 
            (response: any) => {
                const list = response.message?.warehouses 
                    || response.data?.warehouses 
                    || (Array.isArray(response.message) ? response.message : null)
                    || (Array.isArray(response.data) ? response.data : []) 
                    || []; 
                
                const options = list.map((warehouse: any) => ({
                    id: warehouse.id,
                    label: warehouse.name || '',
                }));
                setWarehouseOptions(options);
            },
            (errMessage: any) => { 
                toast.error(typeof errMessage === 'string' ? errMessage : errMessage?.message || "Failed to fetch warehouses"); 
            }
        );
    } else {
      getWarehouseCompanyMapping(
        userCompaniesId,
        (response: any) => {
          try {
            const userWarehouses = response?.user_warehouses || [];
            const warehouses = userWarehouses.map((mapping: any) => ({
              id: mapping.warehouse?.id,
              name: mapping.warehouse?.name,
              client_name: mapping.company?.name,
              active: mapping.warehouse?.active,
              phone: mapping.warehouse?.phone,
              email: mapping.warehouse?.email
            }));
            
            const filteredWarehouses = clientName
              ? warehouses.filter((warehouse: any) =>
                warehouse.client_name?.toLowerCase() === clientName.toLowerCase()
              )
              : warehouses;
              
            const options = filteredWarehouses.map((warehouse: any) => ({
              id: warehouse.id,
              label: warehouse.name || '',
            }));

            setWarehouseOptions(options);
            setWarehouseCount(String(filteredWarehouses.length));

          } catch (error) {
            toast.error('Failed to process warehouse data');
            setWarehouseOptions([]);
            setWarehouseCount('');
          }
        },
        (errMessage: any) => {
          toast.error(errMessage);
          setWarehouseOptions([]);
          setWarehouseCount('');
        }
      );
    }
  };

  const fetchAverageKmPerVehicle = () => {
    if (date === 'today') {
      const today = new Date();
      const nextDay = new Date(today);
      nextDay.setDate(today.getDate() + 1);
      const formattedToday = today.toISOString().split('T')[0];
      const formattedNextDay = nextDay.toISOString().split('T')[0];
      getAverageKmPerVehicle(
        formattedToday,
        formattedNextDay,
        selectedWarehouse ? '' : selectedClient?.id || '',
        selectedWarehouse?.id || '',
        (response: any) => {
          if (response.status === 200) {
            setLowestDistanceToday(response.data.lowest_km != null ? parseFloat(response.data.lowest_km).toFixed(2) : "0.00");
            setHighestDistanceToday(response.data.highest_km != null ? parseFloat(response.data.highest_km).toFixed(2) : '0.00 ');
            setAverageDistanceToday(response.data.average_distance != null ? parseFloat(response.data.average_distance).toFixed(2) : '0.00');
          }
        },
        (errMessage: any) => {
          setLowestDistanceToday("0.00");
          setHighestDistanceToday('0.00 ');
          setAverageDistanceToday('0.00');
          toast.error(errMessage);
        }
      );
    } else if (date === 'this_month') {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const formattedFirstDayOfMonth = firstDayOfMonth.toISOString().split('T')[0];
      const formattedToday = today.toISOString().split('T')[0];
      getAverageKmPerVehicle(
        formattedFirstDayOfMonth,
        formattedToday,
        selectedWarehouse ? '' : selectedClient?.id || '',
        selectedWarehouse?.id || '',
        (response: any) => {
          if (response.status === 200) {
            setLowestDistanceToday(response.data.lowest_km != null ? parseFloat(response.data.lowest_km).toFixed(2) : '0.00');
            setHighestDistanceToday(response.data.highest_km != null ? parseFloat(response.data.highest_km).toFixed(2) : '0.00');
            setAverageDistanceToday(response.data.average_distance != null ? parseFloat(response.data.average_distance).toFixed(2) : '0.00');
          }
        },
        (errMessage: any) => {
          setLowestDistanceToday("0.00");
          setHighestDistanceToday('0.00 ');
          setAverageDistanceToday('0.00');
          toast.error(errMessage);
        }
      );
    }
  };

  const fetchTripStatus = () => {
    let fuelType = '';
    if (showEVStatus) {
      fuelType = 'EV';
    } else if (showICEStatus) {
      fuelType = 'ICE';
    }
    
    getTripStatus(
      selectedWarehouse ? '' : selectedClient?.id || '',
      selectedWarehouse?.id || '',
      fuelType,
      (response: any) => {
        if (response.status === 200) {
          const tripCounts: { [key: string]: { started: number, completed: number } } = {};
          let noTripCount = 0;
          response.data.forEach((vehicle: any) => {
            const tripEnd = vehicle.status.match(/Trip (\d+) ended/);
            const tripProgress = vehicle.status.match(/Trip (\d+) started/);
            if (tripEnd) {
              const tripNumber = tripEnd[1];
              if (!tripCounts[tripNumber]) {
                tripCounts[tripNumber] = { started: 0, completed: 0 };
              }
              tripCounts[tripNumber].completed += 1;
            } else if (tripProgress) {
              const tripNumber = tripProgress[1];
              if (!tripCounts[tripNumber]) {
                tripCounts[tripNumber] = { started: 0, completed: 0 };
              }
              tripCounts[tripNumber].started += 1;
            } else {
              noTripCount += 1;
            }
          });
          const labels: string[] = [];
          const data: number[] = [];
          const backgroundColor: string[] = [];
          const hoverBackgroundColor: string[] = [];
          if (noTripCount > 0) {
            labels.push("No trips started");
            data.push(noTripCount);
            backgroundColor.push('#FF0000');
            hoverBackgroundColor.push('#FF0000');
          }
          Object.keys(tripCounts).forEach(tripNumber => {
            const tripData = tripCounts[tripNumber];
            const ordinalTripNumber = getOrdinal(Number(tripNumber));
            const shadesOfGreenStarted = ['#A8E6A1', '#81C784', '#66BB6A', '#43A047', '#2E7D32', '#0B6B2C'];
            const shadesOfGreenCompleted = ['#6DBB75', '#4CAF50', '#388E3C', '#2A7B2E', '#1B5E20', '#0A4A20'];
            const tripNum = parseInt(tripNumber);
            if (tripNum >= 1 && tripNum <= 5) {
              const index = tripNum - 1;
              if (tripData.started > 0) {
                labels.push(`${ordinalTripNumber} trip in progress`);
                data.push(tripData.started);
                backgroundColor.push(shadesOfGreenStarted[index]);
                hoverBackgroundColor.push(shadesOfGreenStarted[index]);
              }
              if (tripData.completed > 0) {
                labels.push(`${ordinalTripNumber} trip completed`);
                data.push(tripData.completed);
                backgroundColor.push(shadesOfGreenCompleted[index]);
                hoverBackgroundColor.push(shadesOfGreenCompleted[index]);
              }
            } else if (tripNum > 5) {
              const progressLabel = "5+ trips in progress";
              const completedLabel = "5+ trips completed";
              if (tripData.started > 0) {
                const progressIndex = labels.findIndex(label => label === progressLabel);
                if (progressIndex >= 0) {
                  data[progressIndex] += tripData.started;
                } else {
                  labels.push(progressLabel);
                  data.push(tripData.started);
                  backgroundColor.push(shadesOfGreenStarted[5]);
                  hoverBackgroundColor.push(shadesOfGreenStarted[5]);
                }
              }
              if (tripData.completed > 0) {
                const completedIndex = labels.findIndex(label => label === completedLabel);
                if (completedIndex >= 0) {
                  data[completedIndex] += tripData.completed;
                } else {
                  labels.push(completedLabel);
                  data.push(tripData.completed);
                  backgroundColor.push(shadesOfGreenCompleted[5]);
                  hoverBackgroundColor.push(shadesOfGreenCompleted[5]);
                }
              }
            }
          });
          setPieData({
            labels,
            datasets: [
              {
                data,
                backgroundColor,
                hoverBackgroundColor,
              },
            ],
          });
        }
      },
      (errMessage: any) => {
        setPieData({
          labels: ["No Data"],
          datasets: [
            {
              data: [0.01],
              backgroundColor: ['#d3d3d3'],
              hoverBackgroundColor: ['#d3d3d3'],
            },
          ],
        });
        toast.error(errMessage.message);
      }
    );
  };

  const fetchTrips = () => {
    const today = new Date();
    const formattedToday = today.toISOString().split('T')[0];
    getTrips(
      50,
      0,
      formattedToday,
      formattedToday,
      (response: any) => {
        if (response.status === 400) {
          setTripTimingData([])
        } else {
          const tripsList = response.message.trips_list;
          processTripData(tripsList);
        }
      },
      (errMessage: any) => {
        setTripTimingData([])
        toast.error(errMessage.message);
      },
      '',
      selectedWarehouse ? '' : selectedClient?.name || '',
      selectedWarehouse?.name || '',
    );
  }

  const processTripData = (tripsList: any[]) => {
    const timeRangeCounts = [0, 0, 0, 0, 0, 0];
    tripsList.forEach((trip: any) => {
      const startTime = dayjs(trip.start_time).hour();
      if (startTime >= 0 && startTime < 8) {
        timeRangeCounts[0]++;
      } else if (startTime >= 8 && startTime < 10) {
        timeRangeCounts[1]++;
      } else if (startTime >= 10 && startTime < 12) {
        timeRangeCounts[2]++;
      } else if (startTime >= 12 && startTime < 14) {
        timeRangeCounts[3]++;
      } else if (startTime >= 14 && startTime < 16) {
        timeRangeCounts[4]++;
      } else if (startTime >= 16 && startTime < 24) {
        timeRangeCounts[5]++;
      }
    });
    setTripTimingData(timeRangeCounts);
  };

  const fetchFleetCount = () => {
    const today = new Date();
    const sixDaysAgo = new Date(today);
    sixDaysAgo.setDate(today.getDate() - 6); 
    const formattedToday = today.toISOString().split('T')[0];
    const formattedSixDaysAgo = new Date(today.setDate(today.getDate() - 6)).toISOString().split('T')[0];
    
    const freshToday = new Date();
    const freshSixDaysAgo = new Date(freshToday);
    freshSixDaysAgo.setDate(freshToday.getDate() - 6);
    const sevenMonthsAgo = new Date(freshToday);
    sevenMonthsAgo.setMonth(freshToday.getMonth() - 7);

    setDailyVehicleCounts([]);
    setFleetPerMonth({
      labels: [],
      datasets: [],
    });

    getFleetCount(
      fleetCountDate === 'day-wise' ? freshSixDaysAgo.toISOString().split('T')[0] : sevenMonthsAgo.toISOString().split('T')[0],
      freshToday.toISOString().split('T')[0],
      selectedWarehouse ? '' : selectedClient?.id || '',
      selectedWarehouse?.id || '',
      (response: any) => {
        if (response.status === 200) {
          const runningVehicles = response.message.running_vehicles_today;
          const nonRunningVehicles = response.message.non_running_vehicles_today;
          const locations = Object.keys(runningVehicles);
          const runningData = Object.values(runningVehicles) as number[];
          const nonRunningData = locations.map(location => nonRunningVehicles[location] || 0);

          setFleetCountTodayData({
            labels: locations,
            datasets: [
              {
                label: 'Running Vehicles',
                data: runningData,
                backgroundColor: '#52be80',
              },
              {
                label: 'Non-Running Vehicles',
                data: nonRunningData,
                backgroundColor: '#e74c3c',
              }
            ],
          });

          const vehicleCountPerPeriod = response.message.vehicle_count_per_period;

          if (fleetCountDate === 'month-wise') {
            const monthsData = vehicleCountPerPeriod.map((period: any) => ({
              month: period.month,
              year: period.year,
              highest_vehicle_in_day: period.highest_vehicle_in_day || 0,
              average_vehicle_per_day: period.average_vehicle_per_day || 0,
              total_vehicle_days_in_month: period.total_vehicle_days_in_month || 0,
            }));
            const monthYearRange: { month: number; year: number; label: string }[] = [];
            let start = new Date(sevenMonthsAgo.getFullYear(), sevenMonthsAgo.getMonth(), 1);
            const end = new Date(freshToday.getFullYear(), freshToday.getMonth(), 1);

            while (start <= end) {
              monthYearRange.push({
                month: start.getMonth() + 1,
                year: start.getFullYear(),
                label: `${monthNames[start.getMonth() + 1]}`,
              });
              start.setMonth(start.getMonth() + 1);
            }
            const datasets = [];
            if (showHighestVehicle) {
              const highestData = monthYearRange.map(({ month, year }) => {
                const monthData = monthsData.find((data: any) =>
                  data.month === month && data.year === year
                );
                return monthData ? monthData.highest_vehicle_in_day : 0;
              });
              datasets.push({
                label: 'Highest Vehicle',
                data: highestData,
                backgroundColor: '#36A2EB',
              });
            }

            if (showAverageVehicle) {
              const averageData = monthYearRange.map(({ month, year }) => {
                const monthData = monthsData.find((data: any) =>
                  data.month === month && data.year === year
                );
                return monthData ? parseFloat(monthData.average_vehicle_per_day.toFixed(2)) : 0;
              });

              datasets.push({
                label: 'Average Vehicle',
                data: averageData,
                backgroundColor: '#FF6384',
              });
            }

            if (showTotalVehicle) {
              const totalData = monthYearRange.map(({ month, year }) => {
                const monthData = monthsData.find((data: any) =>
                  data.month === month && data.year === year
                );
                return monthData ? monthData.total_vehicle_days_in_month : 0;
              });

              datasets.push({
                label: 'Total Vehicle Days',
                data: totalData,
                backgroundColor: '#4BC0C0',
              });
            }

            setFleetPerMonth({
              labels: monthYearRange.map((item) => item.label),
              datasets: datasets,
            });
          } else {
            const dateRange = [];
            for (let i = 6; i >= 0; i--) {
              const date = new Date(freshToday);
              date.setDate(freshToday.getDate() - i);
              dateRange.push(date.toISOString().split('T')[0]);
            }
            if (Array.isArray(vehicleCountPerPeriod) && vehicleCountPerPeriod.length > 0) {
              const dailyData = dateRange.map(date => {
                const dayData = vehicleCountPerPeriod.find((period: any) =>
                  period.date === date
                );
                return {
                  date,
                  count: dayData ? dayData.vehicle_count || 0 : 0,
                  trip1Count: dayData ? dayData.trip_1_count || 0 : 0,
                  trip2Count: dayData ? dayData.trip_2_count || 0 : 0,
                  trip3Count: dayData ? dayData.trip_3_count || 0 : 0,
                };
              });

              setDailyVehicleCounts(dailyData);
            } else {
              const emptyData = dateRange.map(date => ({
                date,
                count: 0,
                trip1Count: 0,
                trip2Count: 0,
                trip3Count: 0
              }));

              setDailyVehicleCounts(emptyData);
            }
          }
        } else {
          toast.success(response.message);
        }
      },
      (errMessage: any) => {
        setDailyVehicleCounts([]);
        setFleetPerMonth({
          labels: [],
          datasets: [],
        });
        setFleetCountTodayData({
          labels: ["No Data"],
          datasets: [
            {
              label: 'Running Vehicles',
              data: [0],
              backgroundColor: '#52be80',
            },
            {
              label: 'Non-Running Vehicles',
              data: [0],
              backgroundColor: '#e74c3c',
            }
          ],
        });
        toast.error(errMessage.message);
      }
    );
  };

  useEffect(() => {
    if (hasVehicleFleetCount) {
      fetchFleetCount();
    }
  }, [fleetCountDate, selectedClient, selectedWarehouse, showTrips, hasVehicleFleetCount]);

  const handleFleetCountDateChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    setFleetCountDate(newValue);
    if (newValue === 'month-wise') {
      setShowHighestVehicle(true);
      setShowAverageVehicle(false);
      setShowTotalVehicle(false);
    }
  };
  
  const barChartOptions = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { boxWidth: 12 },
        onClick: (e: any, legendItem: any, legend: any) => {
          return false;
        }
      },
      datalabels: {
        display: (context: any) => {
          return context.dataset.data[context.dataIndex] !== 0;
        },
        font: {
          size: 12,
          weight: 'bold' as const
        }
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        beginAtZero: true,
        stacked: fleetCountDate === 'day-wise',
        ticks: {
          stepSize: 1,
        },
      },
      y: {
        beginAtZero: true,
        stacked: fleetCountDate === 'day-wise',
        ticks: {
          stepSize: 1,
          callback: function (value: any) {
            if (Number.isInteger(value)) {
              return value;
            }
            return value;
          },
        },
        min: 0,
      },
    },
  };

  const handleHighestVehicleChange = (checked: boolean) => {
    if (checked) {
      setShowHighestVehicle(true);
      setShowAverageVehicle(false);
      setShowTotalVehicle(false);
    }
  };

  const handleAverageVehicleChange = (checked: boolean) => {
    if (checked) {
      setShowHighestVehicle(false);
      setShowAverageVehicle(true);
      setShowTotalVehicle(false);
    }
  };

  const handleTotalVehicleChange = (checked: boolean) => {
    if (checked) {
      setShowHighestVehicle(false);
      setShowAverageVehicle(false);
      setShowTotalVehicle(true);
    }
  };

  const [pieData, setPieData] = useState({
    labels: [] as string[],
    datasets: [
      {
        data: [] as number[],
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
        hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
      },
    ],
  });
    useEffect(() => {
    if (fleetCountDate === 'month-wise' && (hasVehicleFleetCount || showTrips)) {
      fetchFleetCount();
    }
  }, [showHighestVehicle, showAverageVehicle, showTotalVehicle, fleetCountDate, showTrips, hasVehicleFleetCount]);

  const handleClientChange = (event: SelectChangeEvent<string>) => {
    const clientId = event.target.value;
    const client = clientOptions.find(c => c.id === clientId);
    const transformedClient = client ? { id: client.id, name: client.label } : null;

    if (transformedClient) {
      getVendorClientWarehouseMapping(clientId, (response: any) => {
        const clientVendorWarehouses = response?.client_vendor_warehouses || [];
        const warehouses = clientVendorWarehouses.map((mapping: any) => ({
          id: mapping.warehouse?.id,
          name: mapping.warehouse?.name,
          client_name: mapping.clientvendor?.name,
          active: mapping.warehouse?.active,
          phone: mapping.warehouse?.phone,
          email: mapping.warehouse?.email
        }));
        const filteredWarehouses = transformedClient.name
          ? warehouses.filter((warehouse: any) =>
            warehouse.client_name?.toLowerCase() === transformedClient.name.toLowerCase()
          )
          : warehouses;
        const options = filteredWarehouses.map((warehouse: any) => ({
          id: warehouse.id,
          label: warehouse.name,
        }));
        setWarehouseOptions(options);
        setWarehouseCount(filteredWarehouses.length);
      }, (error: any) => {
        console.log("Error fetching warehouse mappings:", error);
      });
    }
    else {
      if (hasWarehouseFeature) {
        fetchWarehouses('');
      }
    }
    setSelectedClient(transformedClient);
    setSelectedWarehouse(null);
    localStorage.setItem('lastSelectedClient', JSON.stringify(transformedClient));
  };

  const handleWarehouseChange = (event: SelectChangeEvent<string>) => {
    const warehouseId = event.target.value;
    const warehouse = warehouseOptions.find(c => c.id === warehouseId);
    const transformedWarehouse = warehouse ? { id: warehouse.id, name: warehouse.label } : null;
    setSelectedWarehouse(transformedWarehouse);
    localStorage.setItem('lastSelectedWarehouse', JSON.stringify(transformedWarehouse));
  };

  const handleDateChange = (event: SelectChangeEvent<string>) => {
    setDate(event.target.value);
  }

  const dateSelection = [
    { id: 'today', label: 'Today' },
    { id: 'this_month', label: 'This Month' },
  ];
  const fleetDateSelection = [
    { id: 'day-wise', label: 'Day-Wise' },
    { id: 'month-wise', label: 'Month Wise' },
  ];
  const tripTiming = {
    labels: ['12-8', '8-10', '10-12', '12-2', '2-4', '4-12'],
    datasets: [{
      label: 'Number of Trips',
      data: tripTimingData,
      backgroundColor: '#42a5f5',
    }],
  };

  const fleetToday = {
    labels: dailyVehicleCounts.map(entry => {
      const date = new Date(entry.date);
      return `${getOrdinal(date.getDate())} ${monthNames[date.getMonth() + 1]}`;
    }),
    datasets: (() => {
      const datasets = [];
      const hasTrip1Data = dailyVehicleCounts.some(entry => entry.trip1Count > 0);
      if (hasTrip1Data) {
        datasets.push({
          label: 'Trip 1',
          data: dailyVehicleCounts.map(entry => entry.trip1Count || 0),
          backgroundColor: '#a3e7a6ff',
        });
      }

      const hasTrip2Data = dailyVehicleCounts.some(entry => entry.trip2Count > 0);
      if (hasTrip2Data) {
        datasets.push({
          label: 'Trip 2',
          data: dailyVehicleCounts.map(entry => entry.trip2Count || 0),
          backgroundColor: '#75bf79ff',
        });
      }

      const hasTrip3Data = dailyVehicleCounts.some(entry => entry.trip3Count > 0);
      if (hasTrip3Data) {
        datasets.push({
          label: 'Trip 3',
          data: dailyVehicleCounts.map(entry => entry.trip3Count || 0),
          backgroundColor: '#5a945dff',
        });
      }

      if (datasets.length === 0) {
        datasets.push({
          label: 'Trip 1',
          data: dailyVehicleCounts.map(() => 0),
          backgroundColor: '#a3e7a6ff',
        });
      }

      return datasets;
    })(),
  };

  // Defining all the dashboard cards dynamically for iteration
  const dashboardCards = [
    {
      id: 'tripStatus',
      show: hasTripStatus,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" align="center" gutterBottom>Trip Status Today</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <FormControlLabel control={<Checkbox size="small" checked={showEVStatus} onChange={(e) => { setShowEVStatus(e.target.checked); if (e.target.checked) setShowICEStatus(false); }} color="primary" />} label={<Typography variant="body2">EV</Typography>} />
              <FormControlLabel control={<Checkbox size="small" checked={showICEStatus} onChange={(e) => { setShowICEStatus(e.target.checked); if (e.target.checked) setShowEVStatus(false); }} color="primary" />} label={<Typography variant="body2">ICE</Typography>} />
            </Box>
          </Box>
          <Box sx={{ height: 180 }}>
            <Pie data={pieData} options={doughnutOptions} />
          </Box>
        </Paper>
      )
    },
    {
      id: 'vehicleSpeed',
      show: hasVehicleSpeed,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" align="center" gutterBottom>
              Vehicle Speed
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
              <Dropdown
                options={speedDateOptions}
                selectedValue={speedDateFilter}
                handleChange={(e) => setSpeedDateFilter(e.target.value as string)}
                placeholder="Date"
                width="130px"
                height="30px"
                displayEmpty={false}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={speedMode === 'average'} onChange={() => setSpeedMode('average')} color="primary" />}
                  label={<Typography variant="body2">Average</Typography>}
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={speedMode === 'highest'} onChange={() => setSpeedMode('highest')} color="primary" />}
                  label={<Typography variant="body2">Highest</Typography>}
                />
              </Box>
            </Box>
            {speedDateFilter === 'Custom' && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
                <TextField label="From" type="date" size="small" value={customFromDate} onChange={(e) => setCustomFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="To" type="date" size="small" value={customToDate} onChange={(e) => setCustomToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
            )}
            <Box sx={{ height: 180 }}>
              <Bar 
                data={vehicleSpeedData} 
                options={{
                  ...barChartOptions,
                  scales: {
                    ...barChartOptions.scales,
                    x: {
                      ...barChartOptions.scales?.x,
                      ticks: {
                        ...barChartOptions.scales?.x?.ticks,
                        maxRotation: 20,
                        minRotation: 20,
                        autoSkip: false
                      }
                    }
                  }
                }} 
              />
            </Box>
          </Box>
        </Paper>
      )
    },
    {
      id: 'deviceFleetCount',
      show: hasDeviceFleetCount,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" align="center" gutterBottom>Fleet Reported</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
              <Dropdown
                options={fleetDateSelection}
                selectedValue={fleetCountDate}
                handleChange={handleFleetCountDateChange}
                placeholder=""
                width="120px"
                height="30px"
                displayEmpty={true}
              />
            </Box>
          </Box>
          <Box sx={{ height: 180 }}>
            <Bar 
              data={dashboardFleetData} 
              options={barChartOptions} 
            />
          </Box>
        </Paper>
      )
    },
    {
      id: 'vehicleFleetCount',
      show: hasVehicleFleetCount,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" align="center" gutterBottom>Vehicle On Trip</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
              {isCompanyDevice && (
                  <FormControlLabel control={<Checkbox size="small" checked={showTrips} onChange={(e) => setShowTrips(e.target.checked)} color="secondary" />} label={<Typography variant="body2">Trips</Typography>} />
              )}
              <Dropdown options={fleetDateSelection} selectedValue={fleetCountDate} handleChange={handleFleetCountDateChange} placeholder="" width="110px" height="30px" displayEmpty={true} />
              {fleetCountDate === 'month-wise' && (
                <>
                  <FormControlLabel control={<Checkbox size="small" checked={showHighestVehicle} onChange={(e) => handleHighestVehicleChange(e.target.checked)} color="primary" />} label={<Typography variant="body2">High</Typography>} />
                  <FormControlLabel control={<Checkbox size="small" checked={showAverageVehicle} onChange={(e) => handleAverageVehicleChange(e.target.checked)} color="primary" />} label={<Typography variant="body2">Avg</Typography>} />
                  <FormControlLabel control={<Checkbox size="small" checked={showTotalVehicle} onChange={(e) => handleTotalVehicleChange(e.target.checked)} color="primary" />} label={<Typography variant="body2">Total</Typography>} />
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ height: 180 }}>
            <Bar
              key={fleetCountDate}
              data={fleetCountDate === 'day-wise' ? fleetToday : fleetPerMonth}
              options={fleetCountDate === 'day-wise' ? stackedBarOptions : barChartOptions}
              plugins={fleetCountDate === 'day-wise' ? [ChartDataLabels, totalPlugin] : []}
            />
          </Box>
        </Paper>
      )
    },
    {
      id: 'totalKms',
      show: hasTotalKms,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" align="center" gutterBottom>Distance Graph</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={kmDateMode === 'day-wise'} onChange={() => setKmDateMode('day-wise')} color="primary" />}
                label={<Typography variant="body2">Day</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={kmDateMode === 'month-wise'} onChange={() => setKmDateMode('month-wise')} color="primary" />}
                label={<Typography variant="body2">Month</Typography>}
              />
              <Dropdown
                options={fuelOptions}
                selectedValue={kmFuelType}
                handleChange={(e) => setKmFuelType(e.target.value)}
                placeholder="Fuel"
                width="90px"
                height="30px"
              />
              <Dropdown
                options={kmMetricOptions}
                selectedValue={selectedKmMetric}
                handleChange={(e) => setSelectedKmMetric(e.target.value)}
                placeholder="Metric"
                width="110px"
                height="30px"
              />
            </Box>
          </Box>
          <Box sx={{ height: 180 }}>
            <Bar 
              data={totalKmData} 
              options={{
                ...barChartOptions,
                plugins: {
                  ...barChartOptions.plugins,
                  datalabels: {
                    ...barChartOptions.plugins?.datalabels,
                    formatter: (val: number) => `${val}`
                  },
                  tooltip: {
                    callbacks: {
                      afterLabel: function (context: any) {
                        const dataset = context.dataset;
                        if (dataset.customVehicleNumbers && dataset.customVehicleNumbers[context.dataIndex]) {
                          const vehicle = dataset.customVehicleNumbers[context.dataIndex];
                          if (vehicle) {
                            return `Vehicle: ${vehicle}`;
                          }
                        }
                        return '';
                      }
                    }
                  }
                }
              }} 
            />
          </Box>
        </Paper>
      )
    },
    {
      id: 'fleetSplitToday',
      show: hasVehicleFleetCount,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom align="center">Fleet Split Today</Typography>
          <Box sx={{ height: 180, mt: 3 }}>
            <Bar data={fleetCountTodayData} options={stackedBarOptions} plugins={[ChartDataLabels, totalPlugin]} />
          </Box>
        </Paper>
      )
    },
    {
      id: 'tripsFeature',
      show: hasTripsFeature,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom align="center">Trip Timing</Typography>
          <Box sx={{ height: 180, mt: 3 }}>
            <Bar data={tripTiming} options={barChartOptions} />
          </Box>
        </Paper>
      )
    },
    {
      id: 'vehicleCount',
      show: hasVehicleCount,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" align="center" gutterBottom>Vehicle Count</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={isFuelChecked} onChange={handleFuelCheck} color="primary" />}
                label={<Typography variant="body2">Fuel</Typography>}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={isModelChecked} onChange={handleModelCheck} color="primary" />}
                label={<Typography variant="body2">Model</Typography>}
              />
            </Box>

            {isFuelChecked ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', marginBottom: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" gutterBottom align="center">EV</Typography>
                    <IconButton><LocalShippingIcon style={{ color: '#52be80', scale: '1.5' }} /></IconButton>
                    <Typography variant="h6" fontWeight="bold">{dashboardEvCount}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" gutterBottom align="center">Diesel</Typography>
                    <IconButton><LocalShippingIcon style={{ color: 'rgb(233 91 91)', scale: '1.5' }} /></IconButton>
                    <Typography variant="h6" fontWeight="bold">{dieselCount}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" gutterBottom align="center">CNG</Typography>
                    <IconButton><LocalShippingIcon style={{ color: '#36A2EB', scale: '1.5' }} /></IconButton>
                    <Typography variant="h6" fontWeight="bold">{cngCount}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="body1" gutterBottom align="center">Petrol</Typography>
                    <IconButton><LocalShippingIcon style={{ color: '#f4d73f', scale: '1.5' }} /></IconButton>
                    <Typography variant="h6" fontWeight="bold">{petrolCount}</Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 180, overflowY: 'auto', maxWidth: 350, margin: '0 auto', borderRadius: 2, border: '1px solid #e0e0e0', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '4px' }, }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ backgroundColor: '#f8f9fa', fontWeight: 600, color: '#424242', borderBottom: '2px solid #e0e0e0', }}>Model</TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#f8f9fa', fontWeight: 600, color: '#424242', borderBottom: '2px solid #e0e0e0', }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modelData.map((row, index) => (
                      <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa', transition: 'background-color 0.2s', '&:hover': { backgroundColor: '#e3f2fd' }, '&:last-child td, &:last-child th': { border: 0 }, }}>
                        <TableCell align="left" sx={{ color: '#555', fontWeight: 500 }}>{row.make_model}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'inline-block', backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '2px 10px', borderRadius: '12px', fontWeight: 'bold', fontSize: '0.75rem', border: '1px solid #c8e6c9', }}>
                            {row.total}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {modelData.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: '#9e9e9e', fontStyle: 'italic' }}>No model data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      )
    },
    {
      id: 'distanceGraph',
      show: hasDistanceGraph,
      content: (
        <Paper elevation={8} sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" align="center" gutterBottom>Average Km Graph</Typography>
            <Dropdown options={dateSelection} selectedValue={date} handleChange={handleDateChange} placeholder="" width="120px" height="30px" displayEmpty={true} />
          </Box>
          <Box sx={{ height: 180, width: '100%' }}>
            <Box sx={{ height: '70%', width: '80%', margin: '0 auto' }}>
              <Doughnut data={date === 'today' ? AverageKmPerVehicleToday : AverageKmPerVehicleMonth} options={halfDoughnutOptions} />
            </Box>
            <Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
              <Typography variant="body2" sx={{ color: 'black' }}>Lowest: {lowestDistanceToday} km | Avg: {averageDistanceToday} km | Highest: {HighestDistanceToday} km</Typography>
            </Box>
          </Box>
        </Paper>
      )
    }
  ];

  const visibleCards = dashboardCards.filter(card => card.show);
  const totalActive = visibleCards.length;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <CssBaseline />
      <Box sx={{ width: '250px', position: 'fixed', height: '100vh' }}>
        <NavBar selected="Dashboard" />
      </Box>
      <Box>
        <Header />
        <Box sx={{ flexGrow: 1, marginTop: '70px' }}>
          <Heading
            title="Dashboard"
            closeIcon="false"
            leftContent={
              <>
                {hasClientFeature && (
                  <>
                    <Dropdown
                      options={clientOptions}
                      selectedValue={selectedClient?.id || ''}
                      handleChange={(event) => handleClientChange(event)}
                      placeholder="Choose Client"
                      width='190px'
                      height='40px'
                      displayEmpty={true}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'black',
                        fontWeight: 'bold',
                        marginLeft: '30px',
                        marginTop: {
                          xs: '10px',
                          sm: '0px',
                        }
                      }}
                    >
                      Clients: {clientCount}
                    </Typography>
                  </>
                )}
              </>
            }
            rightContent={
              <>
                {hasWarehouseFeature && (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'black',
                        fontWeight: 'bold',
                        marginLeft: '30px',
                        marginTop: {
                          xs: '10px',
                          sm: '0px',
                        }
                      }}
                    >
                      Warehouses: {warehouseCount}
                    </Typography>
                    <Dropdown
                      options={warehouseOptions}
                      selectedValue={selectedWarehouse?.id || ''}
                      handleChange={(event) => handleWarehouseChange(event)}
                      placeholder="Choose Warehouse"
                      width='190px'
                      height='40px'
                      displayEmpty={true}
                    />
                  </>
                )}
              </>
            }
          />
        </Box>
        <Box sx={{
          flexGrow: 1,
          marginLeft: {
            xs: '0px',
            sm: '180px',
          },
        }}>
          <Box sx={{ flexGrow: 1, padding: 2, paddingBottom: { xs: 10, sm: 2 }, overflow: 'auto' }}>
            <Grid container spacing={2} sx={{ height: '100%' }}>
              
              {visibleCards.map((card, index) => {
                // Dynamically assign md grid value
                let mdSize = totalActive <= 4 ? 6 : 4;
                if (totalActive === 5) {
                  mdSize = index < 3 ? 4 : 6;
                }
                
                return (
                  <Grid item xs={12} sm={6} md={mdSize} key={card.id}>
                    {card.content}
                  </Grid>
                );
              })}

            </Grid>
            <QueryWidget />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;